import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  CircularProgress,
  Stack,
  Alert,
  Chip,
} from '@mui/material';
import api from '../../services/api';

type PromptKey =
  | 'food_recognition'
  | 'food_nutrition'
  | 'workout_recognition'
  | 'workout_calories'
  | 'medication_recognition'
  | 'activity_analysis'
  | 'ehi_explanation';

interface SystemPromptMeta {
  key: string;
  title: string;
  description: string;
  group: string;
}

type RunStatus = 'loading' | 'success' | 'warning' | 'error';

interface RunResult {
  status: RunStatus;
  note?: string;
  error?: string;
  raw?: unknown;
  ms?: number;
}

// Мок-дані для живого прогону кожного промпту - реальний продакшн-ендпоінт, безпечний текст, без впливу на дані користувача.
const MOCK_TEXT = {
  food: 'Сніданок: овочевий салат 200г, гречка 150г, чай без цукру',
  workout: 'Біг 30 хвилин у середньому темпі, 3 підходи підтягувань по 10 разів',
  medication: 'Парацетамол 500мг, 2 рази на день після їжі',
};

async function runFoodRecognition() {
  const contextDate = new Date().toISOString();
  const res = await api.post('/ai/food-analyzer/extract', { text: MOCK_TEXT.food, contextDate, force: true });
  return res.data;
}

async function runFoodNutrition() {
  const res = await api.post('/ai/food-analyzer/estimate-nutrition', {
    items: [{ name: 'Гречка', totalAmount: 150, unit: 'г', type: 'food' }],
    force: true,
  });
  return res.data;
}

async function runWorkoutRecognition() {
  const contextDate = new Date().toISOString();
  const res = await api.post('/ai/workout-analyzer/extract', { text: MOCK_TEXT.workout, contextDate, force: true });
  return res.data;
}

async function runWorkoutCalories() {
  const res = await api.post('/ai/workout-analyzer/estimate-calories', {
    items: [{ name: 'Біг', totalAmount: 5, unit: 'км', type: 'cardio' }],
    force: true,
  });
  return res.data;
}

async function runMedicationRecognition() {
  const contextDate = new Date().toISOString();
  const res = await api.post('/ai/medications-analyzer/extract', { text: MOCK_TEXT.medication, contextDate, force: true });
  return res.data;
}

async function runActivityAnalysis() {
  const fmt = (d: Date) => d.toISOString().slice(0, 10);
  const to = new Date();
  const from = new Date(to.getTime() - 30 * 24 * 60 * 60 * 1000);
  const res = await api.post('/reports/activity/ai-analysis', { from: fmt(from), to: fmt(to) });
  return res.data;
}

async function runEhiExplanation() {
  const res = await api.post('/ehi/me/ai-explanation', {});
  return res.data;
}

const RUNNERS: Record<PromptKey, () => Promise<any>> = {
  food_recognition: runFoodRecognition,
  food_nutrition: runFoodNutrition,
  workout_recognition: runWorkoutRecognition,
  workout_calories: runWorkoutCalories,
  medication_recognition: runMedicationRecognition,
  activity_analysis: runActivityAnalysis,
  ehi_explanation: runEhiExplanation,
};

const FALLBACK_TITLES: Record<PromptKey, string> = {
  food_recognition: 'Розпізнавання страв',
  food_nutrition: 'Розрахунок калоражу',
  workout_recognition: 'Розпізнавання активностей',
  workout_calories: 'Розрахунок витрати енергії',
  medication_recognition: 'Розпізнавання препаратів',
  activity_analysis: 'Аналіз активності (день)',
  ehi_explanation: "Пояснення Індексу здоров'я (EHI)",
};

/**
 * `activity_analysis` і `ehi_explanation` (2026-10-06) повертають структурований JSON, а не HTML - перевіряємо,
 * що відповідь дійсно має обіцяну промптом форму (`summary`/`nextSteps`), а не просто "200 OK" із чимось іншим.
 * Для решти ключів (розпізнавання/розрахунок) форма вже захищена `schema` на боці Gemini - додаткова перевірка не потрібна.
 */
function checkShape(key: PromptKey, data: any): { ok: boolean; note?: string } {
  if (key === 'activity_analysis' || key === 'ehi_explanation') {
    const analysis = data?.data;
    const hasShape =
      analysis &&
      typeof analysis.summary === 'string' &&
      Array.isArray(analysis.supported) &&
      Array.isArray(analysis.heldBack) &&
      Array.isArray(analysis.nextSteps);
    return hasShape
      ? { ok: true, note: 'Форма відповіді (summary/supported/heldBack/nextSteps) відповідає контракту.' }
      : { ok: false, note: 'Відповідь прийшла, але НЕ має очікуваної структури (summary/nextSteps) - контракт розʼїхався.' };
  }
  return { ok: true };
}

const STATUS_CHIP: Record<RunStatus, { label: string; color: 'success' | 'warning' | 'error' | 'info' }> = {
  loading: { label: 'Виконується...', color: 'info' },
  success: { label: 'OK', color: 'success' },
  warning: { label: 'Форма не збігається', color: 'warning' },
  error: { label: 'Помилка', color: 'error' },
};

export const DevAiPromptsTest: React.FC = () => {
  const [prompts, setPrompts] = useState<SystemPromptMeta[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [results, setResults] = useState<Record<string, RunResult>>({});
  const [runningAll, setRunningAll] = useState(false);

  useEffect(() => {
    api
      .get('/admin/system-prompts')
      .then(res => setPrompts(res.data?.data ?? []))
      .catch((err: any) => setListError(err.response?.data?.message || err.message || 'Невідома помилка'));
  }, []);

  const runOne = async (key: PromptKey) => {
    setResults(prev => ({ ...prev, [key]: { status: 'loading' } }));
    const started = Date.now();
    try {
      const data = await RUNNERS[key]();
      const shape = checkShape(key, data);
      setResults(prev => ({
        ...prev,
        [key]: { status: shape.ok ? 'success' : 'warning', note: shape.note, raw: data, ms: Date.now() - started },
      }));
    } catch (err: any) {
      setResults(prev => ({
        ...prev,
        [key]: {
          status: 'error',
          error: err.response?.data?.message || err.message || 'Невідома помилка',
          raw: err.response?.data,
          ms: Date.now() - started,
        },
      }));
    }
  };

  const runAll = async () => {
    setRunningAll(true);
    for (const key of Object.keys(RUNNERS) as PromptKey[]) {
      // Послідовно (не Promise.all) - щоб не влупити 7 паралельних запитів до Gemini одночасно й легше читати лог.
      await runOne(key);
    }
    setRunningAll(false);
  };

  // Порядок - за реєстром бекенду (/admin/system-prompts), якщо він завантажився; інакше фіксований список нижче.
  const orderedKeys = useMemo(() => {
    const known = Object.keys(RUNNERS) as PromptKey[];
    const fromRegistry = prompts.map(p => p.key).filter(k => known.includes(k as PromptKey)) as PromptKey[];
    return fromRegistry.length ? fromRegistry : known;
  }, [prompts]);

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
        <Typography variant="h6">Повний прогін усіх AI-промптів (живі ендпоінти, мок-дані)</Typography>
        <Button variant="contained" onClick={runAll} disabled={runningAll}>
          {runningAll ? <CircularProgress size={20} color="inherit" /> : 'Запустити всі'}
        </Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Список і описи промптів - з <code>/admin/system-prompts</code>. Кожна кнопка реально викликає відповідний
        продакшн-ендпоінт з безпечними мок-даними (<code>force: true</code> там, де є кеш - ігнорує кеш, щоб перевірити
        живий виклик Gemini, а не збіг з БД). "Аналіз активності" і "Пояснення EHI" рахуються в добовий ліміт AI-звітів
        і повернуть коректну помилку (403/404), якщо у вашого акаунту нема власної клієнтської ролі чи розрахованого
        Індексу - це теж валідний результат тесту, а не збій сторінки.
      </Typography>

      {listError && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Не вдалось завантажити список промптів з адмінки ({listError}) - показано статичний список нижче.
        </Alert>
      )}

      <Stack spacing={1.5}>
        {orderedKeys.map(key => {
          const meta = prompts.find(p => p.key === key);
          const result = results[key];
          return (
            <Paper key={key} variant="outlined" sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                <Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {meta?.title ?? FALLBACK_TITLES[key]}{' '}
                    <Typography component="span" variant="caption" color="text.secondary">
                      ({key})
                    </Typography>
                  </Typography>
                  {meta?.description && (
                    <Typography variant="body2" color="text.secondary">
                      {meta.description}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {result && <Chip size="small" label={STATUS_CHIP[result.status].label} color={STATUS_CHIP[result.status].color} />}
                  <Button size="small" variant="outlined" onClick={() => runOne(key)} disabled={result?.status === 'loading'}>
                    {result?.status === 'loading' ? <CircularProgress size={18} /> : 'Запустити'}
                  </Button>
                </Box>
              </Box>

              {result && result.status !== 'loading' && (
                <Box sx={{ mt: 1.5 }}>
                  {result.status === 'error' && <Alert severity="error" sx={{ mb: 1 }}>{result.error}</Alert>}
                  {result.status === 'warning' && <Alert severity="warning" sx={{ mb: 1 }}>{result.note}</Alert>}
                  {result.status === 'success' && result.note && (
                    <Alert severity="success" sx={{ mb: 1 }}>
                      {result.note}
                    </Alert>
                  )}
                  <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'action.hover', maxHeight: 260, overflow: 'auto' }}>
                    <Typography component="pre" variant="caption" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', m: 0 }}>
                      {JSON.stringify(result.raw ?? result.error ?? null, null, 2)}
                    </Typography>
                  </Paper>
                  {typeof result.ms === 'number' && (
                    <Typography variant="caption" color="text.secondary">
                      {result.ms} мс
                    </Typography>
                  )}
                </Box>
              )}
            </Paper>
          );
        })}
      </Stack>
    </Box>
  );
};
